import { useEffect, useState } from 'react';
import { env } from 'constants/env';

/**
 * Injects the Adyen SDK script and stylesheet into the document once,
 * and signals when the script is ready via `scriptLoaded`.
 *
 * Idempotent — safe to call from multiple components; both tags are
 * guarded by their `id` so they are never appended more than once.
 */
export const useAdyenScript = (): { scriptLoaded: boolean } => {
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    let link: HTMLLinkElement | undefined;
    let script: HTMLScriptElement | undefined;

    if (!document.getElementById('adyenStylesheet')) {
      link = document.createElement('link');
      link.id = 'adyenStylesheet';
      link.rel = 'stylesheet';
      link.href = `https://checkoutshopper-${env.adyenEnvironment}.adyen.com/checkoutshopper/sdk/${env.adyenVersion}/adyen.css`;
      link.crossOrigin = 'anonymous';
      link.integrity = env.adyenCssHash;
      document.head.appendChild(link);
    }

    if (!document.getElementById('adyenScript')) {
      script = document.createElement('script');
      script.id = 'adyenScript';
      script.src = `https://checkoutshopper-${env.adyenEnvironment}.adyen.com/checkoutshopper/sdk/${env.adyenVersion}/adyen.js`;
      script.async = true;
      script.integrity = env.adyenScriptHash;
      script.crossOrigin = 'anonymous';
      script.onload = () => setScriptLoaded(true);
      document.body.appendChild(script);
    } else {
      // Script tag already exists (e.g. navigating back to this page)
      setScriptLoaded(true);
    }

    return () => {
      if (script && document.body.contains(script)) {
        document.body.removeChild(script);
      }
      if (link && document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  return { scriptLoaded };
};
